import { useQuery } from "@apollo/client";
import { CompanyContainer } from "components/company/v3/CompanyContainers";
import BrazilianCompanyContext from "components/company/v3/context/BrazilianCompanyContext";
import CompanyNavbarLayout from "components/company/v3/layouts/CompanyNavbarLayout";
import { CompanyContextEnum } from "components/company/v3/menu/enums";
import getTickerSymbolSwitchUrl from "components/company/v3/menu/helpers/getTickerSymbolSwitchUrl";
import BrazilianIssuerSeo from "components/company/v3/seo/BrazilianIssuerSeo";
import getMatchedSecurity from "components/company/v3/utils/getMatchedSecurity";
import LocaleContext from "components/i18n/LocaleContext";
import NavbarLayout from "components/layouts/NavbarLayout";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import Redirect from "lib/http/Redirect";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import getBaseUrl from "lib/utils/getBaseUrl";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import NotFound from "pages/404";
import { IPlatformVertical, ISecurityCountry } from "types/graphqlTypes";

interface BrazilianPageLayoutProps {
  tickerSymbol: string;
  children: React.ReactElement;
}

export default function BrazilianPageLayout({ tickerSymbol, children }: BrazilianPageLayoutProps) {
  const { pathname } = useRouter();
  const searchParams = useSearchParams();

  const {
    data: currentData,
    loading,
    previousData,
  } = useQuery<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>(IssuerByTickerSymbolQuery, {
    variables: {
      tickerSymbol,
      country: ISecurityCountry.Br,
    },
    skip: !tickerSymbol || tickerSymbol.length > 8 || /^([0-9]+)$/.test(tickerSymbol),
  });

  const data = currentData || previousData;

  if (loading) {
    return (
      <NavbarLayout>
        <LocaleContext.Provider value="BRA">
          <div className="flex flex-col gap-4 pb-6">
            <LoadingSkeleton count={1} className="h-16 w-full rounded-none" />

            <CompanyContainer>
              <LoadingSkeleton count={1} className="h-96 xl:h-company-box" />
            </CompanyContainer>
          </div>
        </LocaleContext.Provider>
      </NavbarLayout>
    );
  }

  if (!data || !data.issuerByTickerSymbol) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (
    data.issuerByTickerSymbol.__typename === "AmericanCompanyIssuer" ||
    data.issuerByTickerSymbol.__typename === "BrazilianRealEstateFundIssuer"
  ) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (data.issuerByTickerSymbol.__typename === "BrazilianRealEstateFund") {
    return (
      <Redirect httpStatus={301} to={`${getBaseUrl()}/fiis/${data.issuerByTickerSymbol.tickerSymbol.toLowerCase()}/`} />
    );
  }

  const security = getMatchedSecurity(data.issuerByTickerSymbol, tickerSymbol);

  if (!security || security.__typename !== "BrazilianSecurity") {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (tickerSymbol.length === 4 || tickerSymbol !== security.tickerSymbol) {
    const tab = searchParams.get("tab");
    const link = getTickerSymbolSwitchUrl(security.tickerSymbol, IPlatformVertical.Acoes, pathname, tab);

    return <Redirect httpStatus={301} to={link.as} />;
  }

  return (
    <NavbarLayout>
      <LocaleContext.Provider value="BRA">
        <BrazilianCompanyContext.Provider value={data.issuerByTickerSymbol}>
          <CompanyNavbarLayout context={CompanyContextEnum.BrazilianCompany} tickerSymbol={tickerSymbol}>
            <BrazilianIssuerSeo tickerSymbol={tickerSymbol} />
            {children}
          </CompanyNavbarLayout>
        </BrazilianCompanyContext.Provider>
      </LocaleContext.Provider>
    </NavbarLayout>
  );
}
